import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/hooks';
import { LoadingSpinner } from '@/components';
import LandingPage from '@/pages/HomePage';
import { ErrorPage } from '@/pages/ErrorPage';
import { isDev } from './constants';
import { isMobilePhone } from '@/mobile/detection/deviceDetection';
import { SpaceAccessGuard } from '@/components/SpaceAccessGuard';

// Lazy load the Insights page (Module Federation remote)
const InsightsPage = React.lazy(() => import('@/pages/InsightsPage'));

// Lazy load mobile components for code splitting
const MobileHome = React.lazy(() =>
  import('@/mobile/MobileHome').then((module) => ({ default: module.MobileHome })),
);
const MobileStudio = React.lazy(() =>
  import('@/mobile/MobileStudio').then((module) => ({ default: module.MobileStudio })),
);

const KNOWN_PATHS = ['/studio', '/devices', '/live', '/settings', '/insights', '/connect'];

function Routing() {
  const { isAuthenticated, isLoading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = isMobilePhone();
  // Exact match (after normalizing a trailing slash): `/mobile-foo`,
  // `/mobile/settings`, etc. must fall through to the desktop 404 path
  // rather than silently render MobileHome. `/mobile/` is treated as
  // `/mobile` so bookmarked trailing-slash URLs still resolve.
  const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
  const isMobileRoute = normalizedPath === '/mobile' || normalizedPath === '/mobile/studio';

  // Mobile phone redirect logic
  useEffect(() => {
    if (!isPhone) return;
    if (normalizedPath === '/' || normalizedPath === '/studio') {
      navigate(`/mobile/studio${location.search}${location.hash}`, { replace: true });
    }
  }, [isPhone, normalizedPath, location.search, location.hash, navigate]);

  if (isPhone && (normalizedPath === '/' || normalizedPath === '/studio')) {
    return <LoadingSpinner />;
  }

  // Mobile routes
  if (isMobileRoute) {
    if (isLoading || !isAuthenticated) {
      return <LoadingSpinner />;
    }
    return (
      <SpaceAccessGuard>
        <React.Suspense fallback={<LoadingSpinner />}>
          {normalizedPath === '/mobile/studio' ? <MobileStudio /> : <MobileHome />}
        </React.Suspense>
      </SpaceAccessGuard>
    );
  }

  // Auth gate runs BEFORE the 404 check so unauthenticated users can still
  // hit Auth0 login when they land on a URL we don't recognise (e.g.
  // `/1419` without trailing slash on a review-app deploy — router basename
  // mismatch leaves the internal pathname as `/1419`, which isn't in
  // KNOWN_PATHS. Reversing these would have blocked login behind a 404.)
  if (isLoading || !isAuthenticated) {
    return <LoadingSpinner />;
  }

  // Desktop: unknown paths render a 404 below.
  const isKnownPath =
    normalizedPath === '/' ||
    KNOWN_PATHS.some((p) => normalizedPath === p || normalizedPath.startsWith(p + '/')) ||
    (isDev && (normalizedPath === '/demo' || normalizedPath.startsWith('/demo/')));
  if (!isKnownPath) {
    return (
      <ErrorPage error={{ message: 'Page not found', name: '404' }} resetErrorBoundary={() => {}} />
    );
  }

  // Insights page — loaded as a Module Federation remote, rendered without
  // the shared sidebar. The isKnownPath check above already rejects paths
  // like /insightsfoo, so startsWith is safe here.
  if (normalizedPath.startsWith('/insights')) {
    return (
      <SpaceAccessGuard>
        <React.Suspense fallback={<LoadingSpinner />}>
          <InsightsPage />
        </React.Suspense>
      </SpaceAccessGuard>
    );
  }

  return (
    <SpaceAccessGuard>
      <LandingPage />
    </SpaceAccessGuard>
  );
}

export default Routing;
